import { Component, DestroyRef, OnInit, TemplateRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { OptionsList, OptionsListMulti } from 'gung-common';
import { Observable, Subject, catchError, debounceTime, filter, first, forkJoin, map, of, switchMap } from 'rxjs';
import { AuthService } from '../../../../services/auth/auth.service';
import { GungModalService } from '../../../../services/gung-modal/gung-modal.service';
import {
  SALES_INSIGHTS_DIM_PREFIX,
  SalesInsightsCustomerHealthRow,
  SalesInsightsFilterOption,
  SalesInsightsFilterValues,
  SalesInsightsGoal,
  SalesInsightsGoalCustomer,
  SalesInsightsGoalInput,
  SalesInsightsGoalPreview,
  SalesInsightsGoalSalesperson
} from '../../models/sales-insights.model';
import { SalesInsightsActivityService } from '../../services/sales-insights-activity.service';
import {
  SalesInsightsGoalScopeForm,
  salesInsightsGoalDrop,
  salesInsightsGoalPick,
  salesInsightsGoalRestore,
  salesInsightsGoalScopeForm,
  salesInsightsGoalScopeInput
} from '../../utils/sales-insights-goals';
import { salesInsightsFilterLabel } from '../../utils/sales-insights-labels';
import { salesInsightsConstantCase } from '../../utils/sales-insights-tiles';
import { salesInsightsDayKey, salesInsightsParseDate } from '../../utils/sales-insights-activities';
import { formatSalesInsightsAmount, formatSalesInsightsPercent } from '../../utils/sales-insights-format';
import { salesInsightsColor } from '../../utils/sales-insights-theme';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

export type SalesInsightsGoalCustomerFilter = '' | 'open' | 'done';

export type SalesInsightsGoalTypeMode = 'existing' | 'new';

export interface SalesInsightsGoalForm extends SalesInsightsGoalScopeForm {
  id: string | null;
  title: string;
  deadline: string | null;
  startDate: string | null;
  activityTypes: string[];
  activityActions: string[];
  typeMode: SalesInsightsGoalTypeMode;
  newTypeName: string;
}

export interface SalesInsightsGoalDimensionField {
  name: string;
  label: string;
  options: OptionsListMulti[];
}

const ALL = '';
const PALETTE_SIZE = 8;
const PREVIEW_DEBOUNCE_MS = 300;
const CUSTOMER_SEARCH_DEBOUNCE_MS = 250;
const CUSTOMER_SEARCH_MIN_LENGTH = 2;
const DEFAULT_FLAGS = ['noPurchase3m', 'noPurchase1m', 'noContact6m', 'overdue'];

@Component({
  selector: 'lib-sales-insights-goal-progress',
  standalone: false,
  templateUrl: './sales-insights-goal-progress.component.html',
  styleUrl: './sales-insights-goal-progress.component.scss'
})
export class SalesInsightsGoalProgressComponent extends SalesInsightsBaseComponent<SalesInsightsGoal[]> implements OnInit {
  protected translate = inject(TranslateService);
  protected ngbModal = inject(NgbModal);
  protected gungModalService = inject(GungModalService);
  protected authService = inject(AuthService);
  protected activityService = inject(SalesInsightsActivityService);
  private destroy = inject(DestroyRef);

  readonly formatAmount = formatSalesInsightsAmount;

  goals: SalesInsightsGoal[] = [];
  isAdmin = false;

  openGoal: SalesInsightsGoal | null = null;
  goalCustomers: SalesInsightsGoalCustomer[] = [];
  goalCustomersLoading = false;
  goalCustomersError: string | null = null;
  customerSearch = '';
  customerFilter: SalesInsightsGoalCustomerFilter = ALL;
  customerFilterOptions: OptionsListMulti[] = [];
  savingCustomerIds = new Set<string>();
  checkError: string | null = null;
  private goalChanged = false;

  form: SalesInsightsGoalForm | null = null;
  formError: string | null = null;
  saving = false;
  hasOwnType = false;
  salespersonOptions: OptionsListMulti[] = [];
  countryOptions: OptionsListMulti[] = [];
  typeOptions: OptionsListMulti[] = [];
  actionOptions: OptionsListMulti[] = [];
  flagOptions: OptionsListMulti[] = [];
  dimensionFields: SalesInsightsGoalDimensionField[] = [];
  preview: SalesInsightsGoalPreview | null = null;
  previewLoading = false;
  previewError: string | null = null;
  customerQuery = '';
  customerMatches: SalesInsightsCustomerHealthRow[] = [];
  customerNames: { [customerId: string]: string } = {};
  expandedGoalIds = new Set<string>();
  private previewRequests = new Subject<void>();
  private customerQueries = new Subject<string>();

  ngOnInit(): void {
    this.authService
      .getRoles()
      .pipe(
        filter(roles => !!roles),
        first(),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe(roles => (this.isAdmin = roles.includes('ADMIN')));
    this.previewRequests
      .pipe(
        debounceTime(PREVIEW_DEBOUNCE_MS),
        map(() => this.form),
        filter((form): form is SalesInsightsGoalForm => !!form),
        switchMap(form =>
          this.salesInsightsService
            .previewGoal(salesInsightsGoalScopeInput(form))
            .pipe(catchError(err => of({ error: err?.error?.error || err?.error?.message || 'SALES_INSIGHTS_LOAD_FAILED' })))
        ),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe(result => {
        this.previewLoading = false;
        if ('error' in result) {
          this.preview = null;
          this.previewError = result.error;
          return;
        }
        this.previewError = null;
        this.preview = result;
        this.rememberNames(result.customers);
      });
    this.customerQueries
      .pipe(
        debounceTime(CUSTOMER_SEARCH_DEBOUNCE_MS),
        switchMap(query =>
          query.length < CUSTOMER_SEARCH_MIN_LENGTH
            ? of([] as SalesInsightsCustomerHealthRow[])
            : this.salesInsightsService.searchCustomers(query).pipe(catchError(() => of([] as SalesInsightsCustomerHealthRow[])))
        ),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe(rows => {
        this.customerMatches = rows;
        this.rememberNames(rows);
      });
    super.ngOnInit();
  }

  get canManage(): boolean {
    return !!this.options.manage && this.isAdmin;
  }

  get canBook(): boolean {
    return !!this.options.bookActivity;
  }

  get visibleCustomers(): SalesInsightsGoalCustomer[] {
    const term = this.customerSearch.trim().toLocaleLowerCase('sv');
    return this.goalCustomers.filter(
      customer =>
        (this.customerFilter !== 'open' || !customer.done) &&
        (this.customerFilter !== 'done' || customer.done) &&
        (!term ||
          [customer.customerId, customer.customerName, customer.city]
            .filter(value => !!value)
            .some(value => String(value).toLocaleLowerCase('sv').includes(term)))
    );
  }

  protected fetch(filters: SalesInsightsFilterValues): Observable<SalesInsightsGoal[]> {
    const source = this.config.source || { endpoint: 'goals', params: { includeArchived: !!this.options.includeArchived } };
    return this.salesInsightsService.getData<SalesInsightsGoal[]>(source, filters, this.config.accepts);
  }

  protected reloads(): Observable<unknown> {
    return this.dashboardState.changes('goals');
  }

  protected isEmpty(data: SalesInsightsGoal[] | null): boolean {
    return !data?.length && !this.canManage;
  }

  protected onData(data: SalesInsightsGoal[] | null): void {
    this.goals = data || [];
  }

  percent(goal: SalesInsightsGoal): number {
    return goal.total > 0 ? Math.min(100, (goal.done / goal.total) * 100) : 0;
  }

  percentText(goal: SalesInsightsGoal): string {
    return formatSalesInsightsPercent(this.percent(goal));
  }

  salespersonPercent(salesperson: SalesInsightsGoalSalesperson): number {
    return salesperson.total > 0 ? Math.min(100, (salesperson.done / salesperson.total) * 100) : 0;
  }

  hasSalespersonBreakdown(goal: SalesInsightsGoal): boolean {
    return (goal.salespersons?.length || 0) > 1;
  }

  toggleSalespersons(goal: SalesInsightsGoal, event: Event): void {
    event.stopPropagation();
    if (this.expandedGoalIds.has(goal.id)) {
      this.expandedGoalIds.delete(goal.id);
    } else {
      this.expandedGoalIds.add(goal.id);
    }
  }

  barColor(goal: SalesInsightsGoal, index: number): string {
    if (goal.expired) {
      return 'var(--bs-secondary-color)';
    }
    return `rgb(${salesInsightsColor(this.options.color ?? (index % PALETTE_SIZE) + 1)})`;
  }

  openCustomers(goal: SalesInsightsGoal, template: TemplateRef<unknown>): void {
    this.openGoal = goal;
    this.goalChanged = false;
    this.customerSearch = '';
    this.customerFilter = ALL;
    this.customerFilterOptions = this.buildCustomerFilterOptions(ALL);
    this.checkError = null;
    this.loadGoalCustomers(goal);
    this.ngbModal
      .open(template, { size: 'xl', scrollable: true, centered: true })
      .result.finally(() => {
        this.openGoal = null;
        if (this.goalChanged) {
          this.dashboardState.notifyChanged('goals');
        }
      })
      .catch(() => {});
  }

  selectCustomerFilter(selected: string[]): void {
    this.customerFilter = (selected?.[0] as SalesInsightsGoalCustomerFilter) || ALL;
    this.customerFilterOptions = this.buildCustomerFilterOptions(this.customerFilter);
  }

  toggleDone(customer: SalesInsightsGoalCustomer): void {
    this.check(customer, !customer.done, customer.note ?? null);
  }

  // A note alone leaves the done state as it is, so a customer done by activity stays automatic.
  saveNote(customer: SalesInsightsGoalCustomer, value: string): void {
    const note = value.trim() || null;
    if (note !== (customer.note || null)) {
      this.check(customer, null, note);
    }
  }

  hasOverride(customer: SalesInsightsGoalCustomer): boolean {
    return customer.manual !== null && customer.manual !== undefined;
  }

  resetOverride(customer: SalesInsightsGoalCustomer): void {
    const goal = this.openGoal;
    if (!goal || this.savingCustomerIds.has(customer.customerId)) {
      return;
    }
    this.checkError = null;
    this.savingCustomerIds.add(customer.customerId);
    this.salesInsightsService.checkGoalCustomer(goal.id, { customerId: customer.customerId, reset: true }).subscribe({
      next: () => {
        this.savingCustomerIds.delete(customer.customerId);
        this.goalChanged = true;
        this.loadGoalCustomers(goal, true);
      },
      error: err => {
        this.savingCustomerIds.delete(customer.customerId);
        this.checkError = err?.error?.message || 'SALES_INSIGHTS_SAVE_FAILED';
      }
    });
  }

  onBooked(): void {
    if (this.openGoal) {
      this.goalChanged = true;
      this.loadGoalCustomers(this.openGoal, true);
    }
  }

  openForm(goal: SalesInsightsGoal | null, template: TemplateRef<unknown>, event?: Event): void {
    event?.stopPropagation();
    this.form = {
      ...salesInsightsGoalScopeForm(goal?.scope),
      id: goal?.id || null,
      title: goal?.title || '',
      deadline: goal?.deadline || null,
      startDate: goal?.startDate || null,
      activityTypes: [...(goal?.activityTypes || [])],
      activityActions: [...(goal?.activityActions || [])],
      typeMode: 'existing',
      newTypeName: ''
    };
    this.formError = null;
    this.preview = null;
    this.previewError = null;
    this.customerQuery = '';
    this.customerMatches = [];
    this.loadScopeOptions();
    this.loadTypeOptions();
    this.loadGoalOptions();
    this.loadPickedNames();
    this.requestPreview();
    this.ngbModal
      .open(template, { size: 'lg', centered: true, scrollable: true })
      .result.finally(() => (this.form = null))
      .catch(() => {});
  }

  requestPreview(): void {
    if (this.form) {
      this.previewLoading = true;
      this.previewRequests.next();
    }
  }

  selectConditions(field: 'salesperson' | 'country' | 'flags', selected: string[]): void {
    if (this.form) {
      this.form[field] = (selected || []).filter(id => !!id);
      this.requestPreview();
    }
  }

  selectDimension(name: string, selected: string[]): void {
    if (this.form) {
      this.form.dimensions = { ...this.form.dimensions, [name]: (selected || []).filter(id => !!id) };
      this.requestPreview();
    }
  }

  setVisitRequiredOnly(value: boolean): void {
    if (this.form) {
      this.form.visitRequiredOnly = value;
      this.requestPreview();
    }
  }

  searchCustomers(query: string): void {
    this.customerQuery = query;
    this.customerQueries.next(query.trim());
  }

  pickCustomer(customer: SalesInsightsCustomerHealthRow): void {
    if (this.form) {
      salesInsightsGoalPick(this.form, customer.customerId);
      this.customerQuery = '';
      this.customerMatches = [];
      this.requestPreview();
    }
  }

  dropCustomer(customerId: string): void {
    if (this.form) {
      salesInsightsGoalDrop(this.form, customerId);
      this.requestPreview();
    }
  }

  restoreCustomer(customerId: string): void {
    if (this.form) {
      salesInsightsGoalRestore(this.form, customerId);
      this.requestPreview();
    }
  }

  isPicked(customerId: string): boolean {
    return !!this.form?.includeCustomerIds.includes(customerId);
  }

  customerLabel(customerId: string): string {
    const name = this.customerNames[customerId];
    return name ? `${name} (${customerId})` : customerId;
  }

  setDeadline(date: Date | null): void {
    if (this.form) {
      this.form.deadline = date ? salesInsightsDayKey(date) : null;
    }
  }

  deadlineDate(): Date | null {
    return salesInsightsParseDate(this.form?.deadline);
  }

  setStartDate(date: Date | null): void {
    if (this.form) {
      this.form.startDate = date ? salesInsightsDayKey(date) : null;
    }
  }

  startDateValue(): Date | null {
    return salesInsightsParseDate(this.form?.startDate);
  }

  setTypeMode(mode: SalesInsightsGoalTypeMode): void {
    if (this.form && this.form.typeMode !== mode) {
      this.form.typeMode = mode;
      this.typeOptions = this.multiOptions(this.activityService.typeOptions(), this.form.activityTypes);
    }
  }

  selectCriteria(field: 'activityTypes' | 'activityActions', selected: string[]): void {
    if (this.form) {
      this.form[field] = (selected || []).filter(id => !!id);
    }
  }

  saveForm(modal: { close: (result?: unknown) => void }): void {
    const form = this.form;
    if (!form || !form.title.trim()) {
      this.formError = 'SALES_INSIGHTS_GOAL_TITLE_REQUIRED';
      return;
    }
    const createType = form.typeMode === 'new';
    const input: SalesInsightsGoalInput = {
      title: form.title.trim(),
      deadline: form.deadline,
      scope: salesInsightsGoalScopeInput(form),
      startDate: form.startDate,
      activityTypes: createType ? [] : form.activityTypes,
      activityActions: form.activityActions,
      createActivityType: createType,
      newActivityTypeName: createType ? form.newTypeName.trim() || null : null
    };
    this.saving = true;
    this.formError = null;
    (form.id ? this.salesInsightsService.updateGoal(form.id, input) : this.salesInsightsService.createGoal(input)).subscribe({
      next: () => {
        this.saving = false;
        modal.close(true);
        if (createType) {
          this.activityService.reloadTypes(true);
          this.dashboardState.notifyChanged('activityTypes');
        }
        this.dashboardState.notifyChanged('goals');
      },
      error: err => {
        this.saving = false;
        this.formError = err?.error?.error || err?.error?.message || 'SALES_INSIGHTS_SAVE_FAILED';
      }
    });
  }

  deleteGoal(goal: SalesInsightsGoal, event: Event): void {
    event.stopPropagation();
    this.gungModalService
      .openConfirmYesNoModal(
        undefined,
        this.translate.instant('SALES_INSIGHTS_GOAL_DELETE_CONFIRM', { title: goal.title }),
        { size: 'sm' }
      )
      .then(confirmed => {
        if (!confirmed) {
          return;
        }
        this.salesInsightsService.deleteGoal(goal.id).subscribe({
          next: () => {
            this.activityService.reloadTypes();
            this.dashboardState.notifyChanged('activityTypes');
            this.dashboardState.notifyChanged('goals');
          },
          error: err => (this.error = err?.error?.message || 'SALES_INSIGHTS_SAVE_FAILED')
        });
      })
      .catch(() => {});
  }

  private loadGoalCustomers(goal: SalesInsightsGoal, quiet = false): void {
    this.goalCustomersLoading = !quiet;
    this.goalCustomersError = null;
    if (!quiet) {
      this.goalCustomers = [];
    }
    this.salesInsightsService.getGoalCustomers(goal.id, this.filters).subscribe({
      next: customers => {
        this.goalCustomers = (customers || []).sort(
          (a, b) => Number(a.done) - Number(b.done) || (a.customerName || '').localeCompare(b.customerName || '', 'sv')
        );
        if (quiet) {
          goal.done = this.goalCustomers.filter(customer => customer.done).length;
          goal.remaining = Math.max(0, goal.total - goal.done);
        }
        this.goalCustomersLoading = false;
      },
      error: err => {
        this.goalCustomersError = err?.error?.message || 'SALES_INSIGHTS_LOAD_FAILED';
        this.goalCustomersLoading = false;
      }
    });
  }

  // The tick shows at once and is taken back if the save fails, so ticking through a long list never waits.
  // A null done saves the note only.
  private check(customer: SalesInsightsGoalCustomer, done: boolean | null, note: string | null): void {
    const goal = this.openGoal;
    if (!goal || this.savingCustomerIds.has(customer.customerId)) {
      return;
    }
    const previous = {
      done: customer.done,
      note: customer.note ?? null,
      manual: customer.manual ?? null,
      doneSource: customer.doneSource ?? null
    };
    if (done !== null) {
      customer.done = done;
      customer.manual = done;
      customer.doneSource = 'manual';
    }
    customer.note = note;
    this.checkError = null;
    this.savingCustomerIds.add(customer.customerId);
    const request = done === null ? { customerId: customer.customerId, note } : { customerId: customer.customerId, done, note };
    this.salesInsightsService.checkGoalCustomer(goal.id, request).subscribe({
      next: () => {
        this.savingCustomerIds.delete(customer.customerId);
        this.goalChanged = true;
        if (previous.done !== customer.done) {
          goal.done += customer.done ? 1 : -1;
          goal.remaining = Math.max(0, goal.total - goal.done);
        }
      },
      error: err => {
        this.savingCustomerIds.delete(customer.customerId);
        customer.done = previous.done;
        customer.note = previous.note;
        customer.manual = previous.manual;
        customer.doneSource = previous.doneSource;
        this.checkError = err?.error?.message || 'SALES_INSIGHTS_SAVE_FAILED';
      }
    });
  }

  // A goal that already has its own type only picks among the existing ones.
  private loadTypeOptions(): void {
    this.typeOptions = this.multiOptions(this.activityService.typeOptions(), this.form?.activityTypes || []);
    this.hasOwnType = false;
    this.activityService.loadTypes().subscribe(types => {
      const form = this.form;
      if (!form) {
        return;
      }
      this.hasOwnType = !!form.id && types.some(type => type.kind === 'goal' && type.goalId === form.id);
      this.typeOptions = this.multiOptions(this.activityService.typeOptions(), form.activityTypes);
    });
  }

  private loadGoalOptions(): void {
    this.actionOptions = [];
    this.dimensionFields = [];
    this.flagOptions = this.flagMultiOptions(DEFAULT_FLAGS);
    this.salesInsightsService
      .getGoalOptions()
      .pipe(catchError(() => of({ activityActions: [] as SalesInsightsFilterOption[], dimensions: [], flags: DEFAULT_FLAGS })))
      .subscribe(options => {
        this.actionOptions = this.multiOptions(options?.activityActions || [], this.form?.activityActions || []);
        this.flagOptions = this.flagMultiOptions(options?.flags?.length ? options.flags : DEFAULT_FLAGS);
        this.loadDimensionFields(options?.dimensions || []);
      });
  }

  private flagMultiOptions(flags: string[]): OptionsListMulti[] {
    const selected = this.form?.flags || [];
    return flags.map(flag => ({
      id: flag,
      name: this.translate.instant(`SALES_INSIGHTS_FLAG_${salesInsightsConstantCase(flag)}`),
      selected: selected.includes(flag)
    }));
  }

  // Dimensions a stored goal uses but the settings no longer have still show, so saving does not drop them silently.
  private loadDimensionFields(dimensions: string[]): void {
    const names = [...dimensions];
    Object.keys(this.form?.dimensions || {}).forEach(name => !names.includes(name) && names.push(name));
    this.dimensionFields = names.map(name => ({
      name,
      label: salesInsightsFilterLabel(this.translate, `${SALES_INSIGHTS_DIM_PREFIX}${name}`),
      options: this.namedOptions([], this.form?.dimensions[name] || [])
    }));
    this.dimensionFields.forEach(field =>
      this.salesInsightsService
        .getFilterOptions(`${SALES_INSIGHTS_DIM_PREFIX}${field.name}`)
        .pipe(catchError(() => of([] as SalesInsightsFilterOption[])))
        .subscribe(options => (field.options = this.namedOptions(options, this.form?.dimensions[field.name] || [])))
    );
  }

  private loadPickedNames(): void {
    const ids = [...(this.form?.includeCustomerIds || []), ...(this.form?.excludeCustomerIds || [])].filter(
      id => !this.customerNames[id]
    );
    if (!ids.length) {
      return;
    }
    this.salesInsightsService
      .getCustomersById(ids)
      .pipe(catchError(() => of([] as SalesInsightsCustomerHealthRow[])))
      .subscribe(rows => this.rememberNames(rows));
  }

  private rememberNames(rows: SalesInsightsCustomerHealthRow[]): void {
    (rows || []).forEach(row => {
      if (row.customerName) {
        this.customerNames[row.customerId] = row.customerName;
      }
    });
  }

  private multiOptions(options: OptionsList[], selected: string[]): OptionsListMulti[] {
    const known = options.map(option => ({ id: String(option.id), name: String(option.name || option.id) }));
    selected.filter(id => !known.some(option => option.id === id)).forEach(id => known.push({ id, name: id }));
    return known.map(option => ({ ...option, selected: selected.includes(option.id) }));
  }

  private buildCustomerFilterOptions(selected: SalesInsightsGoalCustomerFilter): OptionsListMulti[] {
    return [
      { id: ALL, name: this.translate.instant('SALES_INSIGHTS_GOAL_FILTER_ALL'), selected: selected === ALL },
      { id: 'open', name: this.translate.instant('SALES_INSIGHTS_GOAL_FILTER_OPEN'), selected: selected === 'open' },
      { id: 'done', name: this.translate.instant('SALES_INSIGHTS_GOAL_FILTER_DONE'), selected: selected === 'done' }
    ];
  }

  private loadScopeOptions(): void {
    this.salespersonOptions = this.namedOptions([], this.form?.salesperson || []);
    this.countryOptions = this.namedOptions([], this.form?.country || []);
    const options = (key: string) =>
      this.salesInsightsService.getFilterOptions(key).pipe(catchError(() => of([] as SalesInsightsFilterOption[])));
    forkJoin({ salesperson: options('salesperson'), country: options('country') }).subscribe(result => {
      this.salespersonOptions = this.namedOptions(result.salesperson, this.form?.salesperson || []);
      this.countryOptions = this.namedOptions(result.country, this.form?.country || []);
    });
  }

  private namedOptions(options: SalesInsightsFilterOption[], selected: string[]): OptionsListMulti[] {
    const named = options.map(option => ({
      id: option.id,
      name: option.name && option.name !== option.id ? `${option.name} (${option.id})` : option.id
    }));
    selected.filter(id => !named.some(option => option.id === id)).forEach(id => named.push({ id, name: id }));
    return named
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
      .map(option => ({ ...option, selected: selected.includes(option.id) }));
  }
}
